// Keep browser streaming, the development server and bundled tiles on one
// immutable composite. The 2000 image contains dark acquisition streaks
// across western China; the neighbouring 1999 composite is clean there
// (and the few places where 1999 is not are patched below).
export const EARTH_IMAGERY_DATASET = 'landsat-1999';
export const EARTH_IMAGERY_DATE = '1999-12-01';
export const EARTH_IMAGERY_CACHE = `earth-${EARTH_IMAGERY_DATASET}-v1`;
export const EARTH_IMAGERY_PATH = `/textures/earth-tiles/${EARTH_IMAGERY_DATASET}`;
export const earthImagerySourceAt = (date) => `https://gibs.earthdata.nasa.gov/wmts/epsg4326/best/Landsat_WELD_CorrectedReflectance_TrueColor_Global_Annual/default/${date}/31.25m`;
export const EARTH_IMAGERY_SOURCE = earthImagerySourceAt(EARTH_IMAGERY_DATE);
// The finest level the repository and the deployment hold in full (L3 to L6,
// about 80 MB). Finer tiles are NASA's bytes and stream from NASA; the dev
// server caches them in scripts/data/earth-tile-cache, which git ignores.
// Patched tiles are the exception: every one is committed at every level.
export const EARTH_IMAGERY_SHIPPED_MAX = 6;
// The finest Landsat level a page reads from its own files before asking
// NASA: every level (11) under the dev server's tile route,
// EARTH_IMAGERY_SHIPPED_MAX in a site build, and VITE_EARTH_LOCAL_MAX in a
// build that bundles more (the Qmax desktop app copies the dev cache's levels
// 7 to 11 into its payload, so it keeps working offline). env is
// import.meta.env.
export function earthImageryLocalMax(env = {}) {
  if (env.DEV) return 11;
  const wanted = Number(env.VITE_EARTH_LOCAL_MAX);
  return Number.isInteger(wanted) ? Math.min(11, Math.max(EARTH_IMAGERY_SHIPPED_MAX, wanted)) : EARTH_IMAGERY_SHIPPED_MAX;
}

// Stray scenes in the 1999 composite, painted over with another year's
// composite where that one is clean. NASA serves the same bytes on every
// request, so refetching cannot fix them: scripts/patch-earth-tiles.mjs bakes
// the patch into every bundled tile at levels 3 to 11 (the dev server's tile
// cache applies it to any tile it fetches), and the browser never falls back
// to NASA's copy of a patched tile. `bbox` is [west, south, east, north] in
// degrees and holds the whole scene; the replacement fades out over
// `feather` degrees beyond it (at least PATCH_FEATHER_PX pixels), where the
// two composites are the same mosaic. `scene` is the stray scene's corners
// (NW, NE, SE, SW); `patch-earth-tiles.mjs --check` measures the step across
// them and across the bbox, where the two composites meet.
// `version` goes on the patched tiles' URLs, so a browser or HTTP cache
// holding the old bytes misses them.
export const EARTH_IMAGERY_PATCHES = [
  // Borkou, northern Chad (SW of the Tibesti): one Landsat scene from another
  // acquisition, about 2 x 1.8 degrees, its sand 16% darker and olive, with a
  // detached strip and scan-line artefacts above it. It read as a square in
  // the Sahara from 1.7 Earth radii. The 2000 composite agrees with 1999
  // around it to under one level.
  { id: 'borkou', bbox: [14.03, 17.83, 16.27, 19.71], feather: 0.06, date: '2000-12-01', version: 1,
    scene: [[14.42, 19.42], [16.16, 19.25], [15.87, 17.855], [14.153, 18.104]] },
];
export const PATCH_FEATHER_PX = 2;
const EARTH_ROOT_SPAN = 288;

// The patches touching Landsat tile (z, x, y), each with its feather at z.
export function earthImageryPatchesFor(z, x, y) {
  const span = EARTH_ROOT_SPAN / 2 ** z, west = -180 + x * span, north = 90 - y * span;
  const out = [];
  for (const patch of EARTH_IMAGERY_PATCHES) {
    const [w, s, e, n] = patch.bbox, f = Math.max(patch.feather, PATCH_FEATHER_PX * span / 512);
    if (west < e + f && west + span > w - f && north > s - f && north - span < n + f) out.push({ ...patch, featherDeg: f });
  }
  return out;
}

// '' for an untouched tile, '?patch=N' for one whose bytes a patch changed.
export function earthImageryTileVersion(z, x, y) {
  const patches = earthImageryPatchesFor(z, x, y);
  return patches.length ? `?patch=${Math.max(...patches.map(p => p.version))}` : '';
}
